// MO4.1.1
import React, { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiDeviation } from '@wix/da-papi-types';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import TierProductCardDumb from '../TierProductCardDumb/';
import {
  BiData,
  GiveBadgeAwardBiEvent,
  SubmitButtonClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import BuyCoreBrandedButton from '../../Button/core/dsPresets/BuyCoreBrandedButton';
import SubscribeButton from '../../Button/SubscribeButton';
import { getTierDescription, getTranslatedTierInfo } from '../_helpers';
import { useSubscriptionTier } from '../../utils/hooks/useSubscriptionTier';
import TierStateBadges from '../../Tier/TierStateBadges';
import MoreActionsButton from '../../Tier/MoreActionsButton';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import TierConsumerReduxModule from '../../Tier/redux/consumer/ModuleLoadable';
import { useDeviationMatureFilter } from '../../utils/hooks/useDeviationMatureFilter';
import BoostRootModal from '../../ActionBar/buttons/BoostButton/modals/BoostRootModal';
import HiddenOverlay from '../HiddenOverlay';
import BoostIcon from '@wix/da-ds/pkg/Icons/16x16/BoostFilled';
import { ResourceType } from '../../types/resource';
import { getDeviationSaleDetails } from '../../utils/deviations';
import { useDeviationAttributes } from '../hooks/useDeviationAttributes';

import s from './TierProductCard.scss';

export interface Props {
  deviation: PapiDeviation;
  withExpandButton?: boolean;
  isHighlighted?: boolean;
  className?: string;
  showEmptyPlaceholders?: boolean;
  isCollapsed?: boolean;
  innerRef?: (node: HTMLElement | null) => void;
  onPublish?: (deviation: PapiDeviation) => void;
  onToggleCollapsed?: (isCollapsed: boolean) => void;
  onClick?: () => void;
}

const TierProductCard: React.FC<Props> = ({
  deviation,
  onPublish,
  ...rest
}) => {
  const { t } = useTranslation();
  const [isPublishing, setIsPublishing] = useState(false);
  const [matureFilterOverridden, setMatureFilterOverridden] = useState(false);
  const [isShowingModal, setShowingModal] = useState<boolean>(false);
  const openBoostModal = () => setShowingModal(true);
  const closeBoostModal = () => setShowingModal(false);
  const { isOwner } = useSubscriptionTier(deviation);
  const info = isOwner ? undefined : getTranslatedTierInfo(deviation, t);
  const canPublish = deviation.tier?.canUserPublish;
  const hasDiscord = deviation.tier?.discord?.connected;
  const { isBoosted } = useDeviationAttributes(deviation, isOwner);
  const { eligibleDiscount, allDiscounts } = useMemo(
    () => getDeviationSaleDetails(deviation),
    [deviation]
  );

  const { showBlocked, overrideMatureFilter } =
    useDeviationMatureFilter(deviation);
  const onOverrideFilterClick = useCallback(() => {
    overrideMatureFilter();
    setMatureFilterOverridden(true);
  }, [overrideMatureFilter, setMatureFilterOverridden]);

  let ctaButton: React.ReactNode;
  let secondaryCtaButton: React.ReactNode;

  const onSubmitToTier = useCallback(() => {
    window.location.href = Url.studio({
      openSubmitForm: true,
      tierId: deviation.deviationId,
    });
  }, [deviation]);

  const cover_attachment = useFlag('cover_attachment');

  if (!deviation.tier) {
    return null;
  }

  const onPublishClick = () => {
    if (onPublish) {
      setIsPublishing(true);
      onPublish(deviation);
      // this is a poor man's solution to unlock the button, in case we can't one-click publish (e.g. not all required fields are filled)
      // and open the edit tier popup. if the users closes it, we want to button to render restored in active state.
      setTimeout(() => {
        setIsPublishing(false);
      }, 3000);
    }
  };

  if (isOwner) {
    if (deviation.tier.state === 'draft') {
      if (canPublish) {
        // publishDraftTierSaga
        ctaButton = (
          <>
            <SolidButton
              variant="approval-green"
              size="large"
              onClick={onPublishClick}
              loading={isPublishing}
            >
              {t('deviationview.tierCard.cta.publish')}
            </SolidButton>
            <TierConsumerReduxModule />
          </>
        );
      } else {
        // missing core - currently the only reason the owner cant publish
        ctaButton = (
          <BuyCoreBrandedButton
            size="large"
            biData={
              {
                referral_info: ReferralType.SUBSCRIPTIONS_TIER_DRAFT,
              } as any
            }
            withSaleSparkles
          >
            {t('deviationview.tierCard.cta.getCore')}
          </BuyCoreBrandedButton>
        );
      }
    } else if (deviation.tier.state === 'active') {
      ctaButton = (
        <GhostButton
          variant="main"
          size="large"
          onClick={onSubmitToTier}
          biData={BiData<SubmitButtonClickBiEvent>({
            evid: 704,
            sectionname: 'tiers',
          })}
        >
          {t('deviationview.tierCard.cta.submitToTier')}
        </GhostButton>
      );
      secondaryCtaButton = (
        <GhostButton
          variant="main"
          size="large"
          icon={BoostIcon}
          onClick={openBoostModal}
          disabled={isBoosted}
          biData={BiData<GiveBadgeAwardBiEvent>({
            evid: 320,
            sectionname: 'tiers',
            itemid: deviation.deviationId,
            typeid: ResourceType.TIER,
            badge_type: 'boost',
            badge_typeid: 176,
          })}
        >
          {t(
            isBoosted
              ? 'common.boosted'
              : 'deviationview.tierCard.cta.boostTier'
          )}
        </GhostButton>
      );
    }
  } else {
    ctaButton = (
      <SubscribeButton size="large" tier={deviation} matureFilterDisabled />
    );
  }

  return (
    <div className={s['card-root']}>
      {showBlocked && (
        <HiddenOverlay
          className={s['card-overlay']}
          showBackground={false}
          deviation={deviation}
          overrideMatureFilter={onOverrideFilterClick}
        />
      )}
      <TierProductCardDumb
        info={info}
        coverImage={cover_attachment ? deviation : deviation.coverImage}
        showMatureCover={showBlocked || matureFilterOverridden}
        coverAreaExtrasLeft={
          <TierStateBadges
            deviation={deviation}
            showNeedCoreState
            showPendingDeletionState
          />
        }
        dataHook={deviation.url}
        coverAreaExtrasRight={
          <MoreActionsButton deviation={deviation} size="medium" />
        }
        stats={deviation.tier.stats}
        title={deviation.title!}
        tierState={deviation.tier.state}
        description={getTierDescription(deviation)}
        price={deviation.tier.dollarPrice}
        discount={eligibleDiscount}
        allDiscounts={allDiscounts}
        benefits={deviation.tier.benefits}
        ctaButton={ctaButton}
        secondaryCtaButton={secondaryCtaButton}
        hasDiscord={hasDiscord}
        {...rest}
      />
      {isShowingModal && (
        <BoostRootModal
          deviation={deviation}
          onRequestClose={closeBoostModal}
        />
      )}
    </div>
  );
};
TierProductCard.displayName = 'TierProductCard';

export default TierProductCard;
